/**
 * Série diária do resultado do Book Brasil (#251) — alimenta o sparkline de
 * tendência do card Resultado. Lê do store pnl_historico (read-only); cache
 * module-level CHAVEADO por `dias` + startVisiblePolling, padrão da casa (#75).
 * Erro/vazio → série vazia (o sparkline cai no empty state honesto).
 */
import { useEffect, useState } from "react";
import { getCurvaPnlSerie } from "../api/client";
import type { CurvaPnlSerieResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 10 * 60 * 1000;

const EMPTY: CurvaPnlSerieResponse = {
  serie: [],
  ancoras: { d1_date: null, dsem_date: null, dmes_date: null, dano_date: null },
  anotacoes: [],
};

interface CacheEntry {
  expiresAt: number;
  promise: Promise<CurvaPnlSerieResponse>;
}

const cache = new Map<number, CacheEntry>();

export function invalidateCurvaPnlSerieCache(): void {
  cache.clear();
}

export interface UseCurvaPnlSerieState {
  data: CurvaPnlSerieResponse;
  isLoading: boolean;
  error: Error | null;
}

export function useCurvaPnlSerie(dias = 30): UseCurvaPnlSerieState {
  const [state, setState] = useState<UseCurvaPnlSerieState>({
    data: EMPTY,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    setState((s) => ({ ...s, isLoading: true }));

    function load() {
      const now = Date.now();
      const hit = cache.get(dias);
      if (!hit || hit.expiresAt < now) {
        cache.set(dias, { expiresAt: now + REFETCH_MS, promise: getCurvaPnlSerie(dias) });
      }
      cache
        .get(dias)!
        .promise.then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(dias);
          setState({ data: EMPTY, isLoading: false, error: err });
        });
    }

    load();
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [dias]);

  return state;
}
